import React, { useRef, useState, useEffect } from 'react';
import i18n from 'i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import {AddBoxOutlined, Circle, InstallMobileRounded, IosShareRounded, Tune} from '@mui/icons-material';
import { QrCodeIcon, ScanQRIcon } from '../assets/constants/Icons';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../components/ButtonComponent';
import { CustomBoxInput } from '../components/CustomBoxInput';
import ShowToast from '../components/ToastComponent';
import { useHistory } from 'react-router-dom';
import { ALPHANUM_REGEX, BUTTON_PRIMARY, BUTTON_WARNING, SUCCESS, TOAST_ERROR } from '../assets/constants/Constants';
import {ACCESS_LOCKER_BANK, HOME, ASSIGNMENT_SELECTION, LOGIN, DROP_PICKUP_SELECTION} from '../assets/constants/PageList';
import { useTheme } from '@mui/material';
import CustomQRCodeReader from '../components/CustomQRCodeReader';
import { checkIfAssignmentIsProperlyConfigured, checkAvailableAuthOptions, extractBase64FromUrl, extractDomain, isValidDomainAndLockerBankId, setDomainAndLockerBankIdInLocalStorage, getPathForNextPage, applyBackendLanguageConfig, RenderTabs } from '../lib/Utils';
import { getDomainAndLockerBankIdFromShortCode, getTenantAndAssignmentSettings } from '../api/api';
import CustomDialog from '../components/CustomDialog';
import { ReactComponent as DuroltLogoIcon } from "../assets/images/durolt_logo_new.svg";
import { LockerBanks } from '../components/LockerBanks';
import LockerMapMobile from '../components/LockerMapMobile';
import LockerMapPreview from '../components/LockerMapPreview';
import { storeSecureItemInSpecifiedStorage } from '../lib/BrowserStorageAccessMiddleware';
import { LOCAL_STORAGE } from '../assets/constants/BrowserStorageKeys';


const HomePage = () => {
    const { t } = useTranslation()
    const [installAppPrompt, setInstallAppPrompt] = useState(null);
    const [showInstallAppModal, setShowInstallAppModal] = useState(false);
    const [inputValue, setInputValue] = useState(Array(6).fill(''))
    const inputRefs = useRef([]);
    const [showQRCodeReader, setShowQRCodeReader] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [disabled, setDisabled] = useState(true)
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogContentText, setDialogContentText] = useState('');
    const history = useHistory()
    const theme = useTheme()
    const colorScheme = theme.palette.mode
    const [tabValue, setTabValue] = useState(0);
    const [isFirstTab, setIsFirstTab] = useState(true); // Set to true since tabValue starts at 0
    const [isSecondTab, setIsSecondTab] = useState(true);
    const [firstTabLabel] = useState("Scan to Book");
    const [secondTabLabel] = useState("Enter Code to Book");

    // State for your UI controls
    const [boxesEnable, setBoxesEnable] = useState(false);
    const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
    const [enableScanQR, setEnableScanQR] = useState(true);
    const [type, setType] = useState('Scan');
    
    const handleTabChange = (newValue) => {
        /* 1) reset the state for the code boxes 
        2) hide the code boxes and next button and show the scan QR button
        3) show the code boxes and next button and hide the scan QR button 
        */
       console.log("newValue:",newValue);
        setTabValue(newValue);
        if (newValue === 0) {
            // visible the Scan QR 
            resetInputValue();
            setErrorMessage();
            setBoxesEnable(false);
            setNextButtonEnabled(false);
            setDisabled(true);
            setEnableScanQR(true);
            setType('Scan');
        } else {
            setEnableScanQR(false);
            setBoxesEnable(true);
            setType('Boxes');
            setNextButtonEnabled(true);
        }
    };

    useEffect(() => {
        const handleBeforeInstallAppPrompt = (e) => {
            e.preventDefault();
            setInstallAppPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallAppPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallAppPrompt);
        };
    }, []);

    const resetInputValue = () => {
        setInputValue(Array(6).fill(''));
    };


    const handleChange = (event, index) => {
        const tempVal = [...inputValue];
        let value = event.target.value;

        // Convert to uppercase if the value is an alphabet
        if (isNaN(value) && value.length === 1) {
            value = value.toUpperCase();
        }

        tempVal[index] = value;
        setInputValue(tempVal);

        if (value.length === 1 && index < inputValue.length - 1) {
            inputRefs.current[index + 1].focus();
        }

        const isValid = tempVal.every(digit => digit !== '');
        const codeInput = tempVal.join('');
        let errorMessage = '';

        if (!isValid) {
            errorMessage = t('warning_msg_fill_all_inputs');
        } else if (!ALPHANUM_REGEX.test(codeInput)) {
            errorMessage = t('warning_msg_alphanum_valid_input');
        }

        setErrorMessage(errorMessage);
        setDisabled(!isValid || !ALPHANUM_REGEX.test(codeInput));
    };


    const handleSubmit = async () => {
        try {
                let codeInput = inputValue.join('')
                const responseJson = await getDomainAndLockerBankIdFromShortCode(codeInput)
                if (responseJson.status === SUCCESS) {
                    let lockerBankId = responseJson?.data?.locker_bank_id || null; // temporary locker bank id, we need an API to get the locker bank id and domain
                    let domain = extractDomain(responseJson?.data?.domain)
                    let domainAndLockerBankInfoObject = {
                        "domain": domain,
                        "lockerBankId": lockerBankId
                    }
                    console.log("domainAndLockerBankInfoObject :", domainAndLockerBankInfoObject);
                    setDomainAndLockerBankIdInLocalStorage(domainAndLockerBankInfoObject)
                    if (lockerBankId) {
                        getSettingsValidateAndRedirectUser(lockerBankId);
                    }
                }
        } catch (error) {
            resetInputValue();
            setDisabled(true);
            console.error("HomePage.js - handleSubmitLockerId", error)
        }
    }

    const handleOpenQRCodeReader = () => {
        setShowQRCodeReader(true)
    }

    const qrReaderScanHandler = async (qrCodeData) => {
        try {
            if (qrCodeData && qrCodeData.length > 0) {
                let rawValue = qrCodeData[0]?.rawValue
                let domainAndLockerBankInfoInBase64 = extractBase64FromUrl(rawValue);
                if (isValidDomainAndLockerBankId(domainAndLockerBankInfoInBase64)) {
                    let domainAndLockerBankInformation = window.atob(domainAndLockerBankInfoInBase64);
                    const domainAndLockerBankInfoObject = JSON.parse(domainAndLockerBankInformation);
                    setDomainAndLockerBankIdInLocalStorage(domainAndLockerBankInfoObject);
                    
                    getSettingsValidateAndRedirectUser(domainAndLockerBankInfoObject?.lockerBankId);
                } else {
                    ShowToast(t('error_msg_scanned_qr_not_valid'), TOAST_ERROR)
                    setShowQRCodeReader(false)
                }
            } else {
                ShowToast(t('pleaseScanAgain'), TOAST_ERROR)
            }
        } catch (error) {
            console.error("NewReserveLockerPage.js - qrReaderScanHandler", error)
        }
    }

    const getSettingsValidateAndRedirectUser = async (lockerBankId) => {
        try {
            const response = await getTenantAndAssignmentSettings(lockerBankId);
            applyBackendLanguageConfig(response);

            if (response.status === SUCCESS) {
                // store locker slug 
                const lockerslug = response?.data?.locker_bank?.slug;
                storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, "locker_slug", lockerslug);
                
                console.log("locker slug is :", lockerslug);

                if (response?.data?.locker_assignments.length > 1) {
                    history.push({
                        pathname: '/assignments',
                        state: {
                            tenantAndAssignmentSettings: response?.data
                        }
                    });
                }
                else if (response?.data?.locker_assignments.length === 1) {
                    const isAssignmentProperlyConfigured = await checkIfAssignmentIsProperlyConfigured(response?.data);
                    
                    if (isAssignmentProperlyConfigured === true) { 
                        const redirectionPath = getPathForNextPage(ACCESS_LOCKER_BANK, LOGIN);
                        history.push({
                            pathname: redirectionPath
                        });
                    }
                    else {
                        resetInputValue();
                        setDialogContentText( t(isAssignmentProperlyConfigured?.errorMessage) );
                        setDialogVisible(true);
                    }
                }
                else {
                    resetInputValue();
                    setDialogContentText( t('noLockerAssignments') );
                    setDialogVisible(true);
                }
            }
        } catch (error) {
            resetInputValue();
            setDisabled(true);
            console.error('Error while getting settings and validation: ', error);
        }
    }

    const handleCloseModal = () => {
        setDisabled(true)
        setDialogVisible(false)
    }

    const handleInstallAppButtonClick = async () => {
        const userAgent = navigator.userAgent;
        const isAppleDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isSafariBrowser = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

        if (isAppleDevice && isSafariBrowser) {
            setShowInstallAppModal(true);
        }
        else if (installAppPrompt) {
            try {
                installAppPrompt.prompt();

                setInstallAppPrompt(null);
            } catch (error) {
                console.error('Install app prompt error: ', error);
            }
        }
        else if (window.matchMedia('(display-mode: standalone)').matches) {
            alert('The app is already installed');
        }
        else {
            alert('Installation not available.');
        }
    };

    const renderInstallAppModalContent = () => {
       <div class="modal-content">
            <p class="install-text">To install the app from iOS Safari browser:</p>
            <div class="instructions-list">
                <p class="instruction">
                    1. Tap the <b>Share button</b> <span class="instruction-icon material-icons">ios_share</span> at the bottom bar
                </p>
                <p class="instruction">
                    2. In the options visible, click <b>Add to Home Screen</b> <span class="instruction-icon material-icons">add_box</span>
                </p>
                <p class="instruction">
                    3. On the next dialog, click <b>Add</b>
                </p>
            </div>
        </div>
    };

    const RenderQRoption = ()=>{
        return(
            <>
            <div className='Container-ScanQR-Text' onClick={handleOpenQRCodeReader}>
                {/* container to contain the QR ICON */}
                <div className='Sub-Container-ScanQR-QR'>
                    <ScanQRIcon/>
                </div>
                {/* container to contain the QR containt */}
                <div className='Sub-Container-ScanQR-Text'>
                    <h1 className='heading'>Scan to book</h1>
                    <p className='paragraph'>Scan the QR code on the locker </p>
                </div>
            </div>
            </>
        )
    }

    return (
        <div className='home-page' maxWidth="xs">
            <div className="main-container">
                {showQRCodeReader ?
                    <div className="qr-code-reader">
                        <CustomQRCodeReader
                            handleOnScan={qrReaderScanHandler}
                        />
                        <p className='qr-code-heading'>
                            {t('accessLockerBankByQR')}
                        </p>
                        <ButtonComponent handleClick={() => setShowQRCodeReader(false)} title={t('button_cancel')} type={BUTTON_WARNING} />
                    </div>
                    :
                    <>
                        <div className="logo_style">
                            <DuroltLogoIcon width={180} height={60} />
                        </div>
                        <div className='Container-text'>
                            <p className={`heading ${colorScheme === 'dark' ? 'dark-text' : ""}`}>
                            {t('enterLockerBankId')}
                            </p>
                            <p className='sub-heading'>
                                {t('accessLockerBankText')}
                            </p>
                        </div>
                        
                        <div className='input-tab-bar-container'>
                            <RenderTabs
                                isFirstTab={isFirstTab}
                                isSecondTab={isSecondTab}
                                FirstTabLabalName={firstTabLabel}
                                SecondTabLabalName={secondTabLabel}
                                tabValue={tabValue}
                                onChange={handleTabChange}
                                t={t}
                            />
                        </div>
                        {boxesEnable ? 
                        <>
                            <div className="input-box-view">
                                <CustomBoxInput inputValue={inputValue} handleChange={handleChange} inputRefs={inputRefs} error={!!errorMessage} handleSubmitEvent={handleSubmit} disabled={disabled} customClass='input-box-placeholder' />
                                {errorMessage && <Typography className="error-message">{errorMessage}</Typography>}
                            </div> 
                            <ButtonComponent handleClick={handleSubmit} title={t('continueBtnText')} customClass='' type={'default'} buttonId='continue-button' disabled={disabled}/>
                        </>
                        : 
                            RenderQRoption()
                        } 
                        <div className='locker-map-container'>
                            <LockerMapPreview />                        
                        </div>
                        <ButtonComponent 
                            handleClick={()=>{}} 
                            title={t('Login')} 
                            customClass='' 
                            type={'default'} 
                            buttonId='login-button'
                        />
                        <p className="version-text">
                            {t('appVersionText')} {process.env.REACT_APP_RELEASE_VERSION}
                        </p>
                    </>

                }
                <CustomDialog
                    dialogId='error-dialog'
                    dialogVisible={dialogVisible}
                    handleAccept={handleCloseModal}
                    dialogTitle={t('page_title_error')}
                    dialogContentText={t(dialogContentText)}
                    buttonTwoTitle={t('button_ok')}
                    buttonProps={{ buttonTwo: { type: BUTTON_PRIMARY } }}
                />
                <CustomDialog
                    dialogId='install-app-instructions-modal'
                    dialogVisible={showInstallAppModal}
                    dialogTitle={t('Install App')}
                    children={renderInstallAppModalContent}
                    buttonTwoTitle={t('button_ok')}
                    buttonProps={{buttonTwo: {type: BUTTON_PRIMARY}}}
                    handleAccept={() => setShowInstallAppModal(false)}
                    onClose={() => setShowInstallAppModal(false)}
                />
            </div>
        </div>
    );
}

export default HomePage;
